import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Segment, Header, Grid, Form, Button, Table, Message, Input, Icon } from 'semantic-ui-react';
import { additives } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { AdditiveForm } from './AdditiveForm';

export function AdditivesList() {
  const { user } = useAuth();
  const [additivesList, setAdditivesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedAdditive, setSelectedAdditive] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('ascending');

  useEffect(() => {
    loadAdditives();
  }, []);

  const loadAdditives = async () => {
    try {
      setLoading(true);
      const response = await additives.list();
      setAdditivesList(response.data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load additives. Please try again.');
      console.error('Error loading additives:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    try {
      setLoading(true);
      const response = await additives.search(query);
      setAdditivesList(response.data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to search additives. Please try again.');
      console.error('Error searching additives:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    const isAscending = sortField === field && sortDirection === 'ascending';
    setSortDirection(isAscending ? 'descending' : 'ascending');
    setSortField(field);
    
    const sorted = [...additivesList].sort((a, b) => {
      const aValue = a[field]?.toLowerCase() || '';
      const bValue = b[field]?.toLowerCase() || '';
      return isAscending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
    });
    
    setAdditivesList(sorted);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this additive?')) return;
    
    try {
      await additives.delete(id);
      setAdditivesList(additivesList.filter(a => a.id !== id));
      setError('');
    } catch (err) {
      setError('Failed to delete additive. Please try again.');
      console.error('Error deleting additive:', err);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (selectedAdditive) {
        await additives.update(selectedAdditive.id, formData);
        setAdditivesList(additivesList.map(a => 
          a.id === selectedAdditive.id ? { ...a, ...formData } : a
        ));
      } else {
        const response = await additives.create(formData);
        setAdditivesList([...additivesList, response.data.data]);
      }
      setShowForm(false);
      setSelectedAdditive(null);
      setError('');
    } catch (err) {
      setError('Failed to save additive. Please try again.');
      console.error('Error saving additive:', err);
    }
  };

  return (
    <Container>
      <Segment vertical>
        <Helmet>
          <title>Miakara Soap Recipes - Additives</title>
        </Helmet>
        
        <Grid stackable columns={2}>
          <Grid.Column width={16}>
            <Header as="h1">Additives</Header>
            {error && <Message negative content={error} />}
            
            <Form>
              <Form.Group>
                <Form.Field width={6}>
                  <Input
                    icon="search"
                    placeholder="Search additives..."
                    value={searchQuery}
                    onChange={handleSearch}
                    fluid
                  />
                </Form.Field>
                <Form.Field>
                  <Button
                    primary
                    icon
                    labelPosition="left"
                    onClick={() => {
                      setSelectedAdditive(null);
                      setShowForm(true);
                    }}
                  >
                    <Icon name="plus" />
                    Add New Additive
                  </Button>
                </Form.Field>
              </Form.Group>
            </Form>

            {showForm && (
              <Segment>
                <AdditiveForm
                  additive={selectedAdditive}
                  onSubmit={handleFormSubmit}
                  onCancel={() => {
                    setShowForm(false);
                    setSelectedAdditive(null);
                  }}
                />
              </Segment>
            )}

            <Table celled sortable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    sorted={sortField === 'name' ? sortDirection : null}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortField === 'description' ? sortDirection : null}
                    onClick={() => handleSort('description')}
                  >
                    Description
                  </Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {additivesList.map((additive) => (
                  <Table.Row key={additive.id}>
                    <Table.Cell>{additive.name}</Table.Cell>
                    <Table.Cell>{additive.description}</Table.Cell>
                    <Table.Cell>
                      <Button.Group size="tiny">
                        <Button
                          icon="edit"
                          onClick={() => {
                            setSelectedAdditive(additive);
                            setShowForm(true);
                          }}
                        />
                        <Button
                          icon="trash"
                          color="red"
                          onClick={() => handleDelete(additive.id)}
                          disabled={additive.stats?.recipes?.count > 0}
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Grid.Column>
        </Grid>
      </Segment>
    </Container>
  );
} 